import genericRepositoryService from "../../../services/genericRepositoryService";
import { ISearchLessonRequest } from "../../../models/ISearchLessonRequest";
import { MicroLessonFilter } from "./types";
import { PAGE_SIZE } from "./constants";
import { ISearchLessonResponse } from "../../../models/ISearchLessonResponse";

const FIRST_CONTENT_PAGE_INDEX = 4;
const PINNED_END_PAGE_COUNT = 3;

export const getMicroLessonInsertionIndex = (selectedPageOrder: number, totalPages: number): number => {
  const endOfContentIndex = totalPages - PINNED_END_PAGE_COUNT;

  if (selectedPageOrder < FIRST_CONTENT_PAGE_INDEX) {
    return FIRST_CONTENT_PAGE_INDEX;
  }

  return Math.min(selectedPageOrder + 1, endOfContentIndex);
};

export const fetchMicroLessons = async (
  filters: MicroLessonFilter[],
  isShared: boolean,
  pageNumber = 1,
  pageSize = PAGE_SIZE,
) => {
  const filter: ISearchLessonRequest["filter"] = {};

  filters.forEach((item) => {
    const value = item.value.trim();

    if (item.type === "name") {
      filter.title = value;
      return;
    }

    if (item.type === "tags") {
      filter.tags = filter.tags ? `${filter.tags},${value}` : value;
      return;
    }

    if (item.type === "lessonId") {
      filter.lessonId = value;
      return;
    }

    if (item.type === "objectives") {
      filter.objectives = filter.objectives ? `${filter.objectives},${value}` : value;
      return;
    }

    if (item.type === "from") {
      filter.startDate = value.split(":")[1].trim();
      return;
    }

    if (item.type === "to") {
      filter.endDate = value.split(":")[1].trim();
    }
  });

  const queryParams = new URLSearchParams();
  queryParams.set("pageNumber", pageNumber.toString());
  queryParams.set("pageSize", pageSize.toString());
  queryParams.set("includeMicrolesson", "true");
  queryParams.set("includeModule", "false");
  queryParams.set("isShared", isShared.toString());

  if (filter.title) {
    queryParams.set("name", filter.title);
  }

  if (filter.lessonId) {
    queryParams.set("lessonId", filter.lessonId);
  }

  if (filter.objectives) {
    const objectivesArray = filter.objectives.split(",");
    objectivesArray.forEach((objective) => {
      queryParams.append("objectives", objective);
    });
  }

  if (filter.tags) {
    const tagsArray = filter.tags.split(",");
    tagsArray.forEach((tag) => {
      queryParams.append("tagKeys", tag);
    });
  }

  if (filter.startDate) {
    queryParams.set("startDate", filter.startDate);
  }

  if (filter.endDate) {
    queryParams.set("endDate", filter.endDate);
  }

  /* Set lesson designer version */
  queryParams.set("lessonDesignerVersion", "v2");

  const queryString = queryParams.toString();
  console.log("Fetching micro lessons with query:", queryString);
  return await genericRepositoryService.searchLesson(queryString);
};

export const addMicroLesson = async (microlessonIds: number[]) => {
  return await genericRepositoryService.cloneMicroLesson({ microlessonIds });
};

export const normalizeSearchResponse = (data: ISearchLessonResponse["data"], context: { lessonId: number | null }) => {
  return data.map((item) => ({
    ...item,
    disabled: context.lessonId?.toString() === item.lessonId.toString(),
  }));
};

/**
 * Checks if any micro lesson page has secondary/alternative audio
 * Works for both add micro lesson response (array of items with pageManifest.Pages)
 * and update micro lesson response (single item with pageManifest.Pages)
 */
export const hasMicroLessonSecondaryAudio = (pages: any[]): boolean => {
  for (const page of pages) {
    const narrationAudios = page?.PageManifest?.narrationAudios || page?.pageManifest?.narrationAudios || [];
    for (const audio of narrationAudios) {
      if (audio?.language === "secondary") {
        return true;
      }
    }
  }
  return false;
};
